import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { siteConfigService } from '../services/api';
import type { SiteConfigData } from '../types';

type SiteConfigContextType = {
  config: SiteConfigData;
  loading: boolean;
  refresh: () => Promise<void>;
};

const defaultConfig: SiteConfigData = {};
const SiteConfigContext = createContext<SiteConfigContextType>({
  config: defaultConfig,
  loading: true,
  refresh: async () => {},
});

export function SiteConfigProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfig] = useState<SiteConfigData>(defaultConfig);
  const [loading, setLoading] = useState(true);

  const applyTheme = useCallback((c: SiteConfigData) => {
    const theme = c.theme;
    if (!theme) return;
    const root = document.documentElement.style;
    const set = (cssVar: string, value: string | undefined) => {
      if (value) root.setProperty(cssVar, value);
    };
    set('--gold-primary', theme.goldPrimary ?? theme.buttonPrimaryColor);
    set('--gold-hover', theme.goldHover);
    set('--gold-light', theme.goldLight);
    set('--dragon-blue', theme.dragonBlue ?? theme.buttonSecondaryColor);
    set('--dragon-blue-dark', theme.dragonBlueDark);
    set('--dragon-blue-light', theme.dragonBlueLight);
    set('--bg-dark', theme.bgDark);
    set('--bg-card', theme.bgCard);
    set('--bg-card-hover', theme.bgCardHover);
    set('--text-primary', theme.textPrimary);
    set('--text-secondary', theme.textSecondary);
    set('--text-muted', theme.textMuted);
    set('--gold-glow', theme.goldGlow);
    set('--border-light', theme.borderLight);
    set('--border-gold', theme.borderGold);
    if (theme.gradientStart && theme.gradientEnd) {
      root.setProperty('--gradient-theme', `linear-gradient(135deg, ${theme.gradientStart}, ${theme.gradientEnd})`);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const data = await siteConfigService.getConfig();
      setConfig(data);
      applyTheme(data);
    } catch {
      setConfig({});
    } finally {
      setLoading(false);
    }
  }, [applyTheme]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <SiteConfigContext.Provider value={{ config, loading, refresh }}>
      {children}
    </SiteConfigContext.Provider>
  );
}

export function useSiteConfig(): SiteConfigContextType {
  return useContext(SiteConfigContext);
}
